import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { forgotPasswordSchema, type ForgotValues } from "@/lib/schemas";
import { api, formatApiError } from "@/lib/api";
import AuthShell from "@/layouts/AuthShell";
import { Button, Input, Label, toast } from "@/components/ds";

export default function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: ForgotValues) => {
    try {
      await api.forgotPassword(values.email);
      setSent(true);
      toast.success("Check your email");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AuthShell title="Reset your password" subtitle="We'll send a reset link if the account exists.">
      {sent ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-sent">
          If that account exists, a reset link has been sent. It expires in one hour.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="email-input" {...register("email")} />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="forgot-submit">
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
          </Button>
        </form>
      )}
      <p className="mt-4 text-center text-sm text-slate-500">
        <Link to="/login" className="text-blue-600 hover:text-blue-700" data-testid="back-to-login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
